import { inject, Service } from "@angular/core";
import { httpsCallable } from "firebase/functions";
import { FIREBASE_FUNCTIONS } from "@dis/firebase";

/** One test graph's CSV from a build's Drive folder, as returned by the getBuildTestGraphs callable. */
export interface BuildTestGraph {
  id: string;
  name: string;
  createdTime: string | null;
  csv: string;
}

/**
 * Build test graphs (pressure/force charts) through the getBuildTestGraphs / uploadBuildTestGraph callables, which
 * read and write the build's Drive folder as the Drive service account (creating and linking the folder when the
 * build has none). Server-side so testers and viewers don't need a Google account, token or Drive sharing of their
 * own. Shared by pressure-test and JTMS.
 */
@Service()
export class BuildTestGraphs {
  readonly #functions = inject(FIREBASE_FUNCTIONS);
  readonly #get = httpsCallable<{ buildNumber: string }, { folderId: string | null; graphs: BuildTestGraph[] }>(
    this.#functions,
    "getBuildTestGraphs",
  );
  readonly #upload = httpsCallable<
    { buildNumber: string; title: string; pngBase64: string; csv: string },
    { folderId: string; pngFileId: string | null; csvFileId: string | null }
  >(this.#functions, "uploadBuildTestGraph");

  async list(buildNumber: string): Promise<BuildTestGraph[]> {
    const { data } = await this.#get({ buildNumber });
    return data.graphs;
  }

  /** Uploads a finished chart (a `data:image/png;base64,…` URI) and its CSV. Returns the build's folder id. */
  async upload(buildNumber: string, title: string, pngDataUri: string, csv: string): Promise<string> {
    const { data } = await this.#upload({
      buildNumber,
      title,
      pngBase64: pngDataUri.slice(pngDataUri.indexOf(",") + 1),
      csv,
    });
    return data.folderId;
  }
}

/** CSV text to data rows, header dropped (the shape the Drive-era getCsvFile returned). */
export const csvRows = (csv: string): string[][] =>
  csv
    .split(/\r?\n/)
    .slice(1)
    .filter((row) => row.length > 0)
    .map((row) => row.split(","));
